import { WarrantCode } from '../models';
import LSM from '../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../modules/cache/local-storage.constant';
import apiManage from '../request';
import { RoutesType, genRoutes } from '../router.config';

/**
 * 展平路由树，便于按路径检索目标路由。
 * @param routes - 路由树
 * @returns 展平后的路由列表
 */
function flatRoutes(routes: RoutesType[]): RoutesType[] {
    // 展平后的路由结果，包含所有层级节点。
    const flattenedRoutes: RoutesType[] = [];

    routes.forEach((route) => {
        flattenedRoutes.push(route);
        if (route.children?.length) {
            flattenedRoutes.push(...flatRoutes(route.children));
        }
    });

    return flattenedRoutes;
}

/**
 * 归一化路径，去掉 query string，便于和路由表做匹配。
 * @param path - 原始路径
 * @returns 不含 query 的路径
 */
function normalizePath(path: string): string {
    return path.split('?')[0];
}

/**
 * 根据当前路径匹配最精确的路由配置。
 * @param path - 当前访问路径
 * @returns 命中的路由配置
 */
export function getRouteByPath(path: string): RoutesType | undefined {
    // 预排序后的路由列表，优先匹配更长的路径，避免 `/main` 抢先命中。
    const sortedRoutes = flatRoutes(genRoutes()).sort((leftRoute, rightRoute) => rightRoute.path.length - leftRoute.path.length);
    // 去掉 query 后的实际业务路径。
    const normalizedPath = normalizePath(path);

    return sortedRoutes.find((route) => normalizedPath === route.path || normalizedPath.startsWith(`${route.path}/`));
}

/**
 * 获取当前路径对应的餐厅授权点。
 * @param path - 当前访问路径
 * @returns 路由声明的授权点
 */
export function getRequiredRestaurantWarrantCode(path: string): WarrantCode | undefined {
    return getRouteByPath(path)?.requiredRestaurantWarrantCode;
}

/**
 * 读取默认餐厅 string_id，供刷新页面时兜底使用。
 * @returns 默认餐厅 string_id
 */
function getDefaultRestaurantStringId(): string {
    // 本地缓存中的默认餐厅，用于恢复刷新后的当前餐厅上下文。
    const defaultRestaurant = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT);

    return defaultRestaurant?.string_id || '';
}

/**
 * 判断当前路径在指定餐厅下是否具备授权。
 * @param path - 当前访问路径
 * @param restaurantStringId - 当前餐厅 string_id
 * @returns `true` 表示允许，`false` 表示无权限或本次校验失败
 */
export async function hasRestaurantWarrantAccess(path: string, restaurantStringId?: string): Promise<boolean> {
    // 当前路由声明的餐厅授权点；未声明则默认放行。
    const requiredWarrantCode = getRequiredRestaurantWarrantCode(path);

    if (!requiredWarrantCode) {
        return true;
    }

    // 优先使用显式传入的餐厅，其次回退到本地缓存的默认餐厅。
    const targetRestaurantStringId = restaurantStringId || getDefaultRestaurantStringId();

    if (!targetRestaurantStringId) {
        return false;
    }

    try {
        // 后端授权点校验结果，`passedWarrant` 非空表示已通过。
        const warrantCheckResult = await apiManage.check_warrant_code({
            string_id: targetRestaurantStringId,
            code: requiredWarrantCode,
        });

        if (warrantCheckResult?.error) {
            return false;
        }

        return !!warrantCheckResult.passedWarrant?.length;
    } catch (error) {
        return false;
    }
}
